import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import Select, { ActionMeta } from 'react-select';
import LoadingSpinner from '../loading-spinner/loadingSpinner';

const Dropdown = ({
    initialOptions,
    onSelect,
    placeholder,
    placeholderTitle,
    id,
    isDisabled,
    selectValue,
    originalOptions = null,
    separatorIndex = 0,
    isLoading = false,
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isFocusedModal, setIsFocusedModal] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [options, setOptions] = useState([]);

    const initialOptionsFiltered = useMemo(
        () => [...new Map(initialOptions?.map((item) => [item.value, item])).values() as any],
        [initialOptions],
    );

    useEffect(() => {
        setOptions(initialOptionsFiltered as any);
    }, [initialOptionsFiltered]);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    useEffect(() => {
        if (isFocused && isMobile) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isFocused, isMobile]);

    const handleFocus = () => setIsFocused(true);
    const handleFocusModal = () => setIsFocusedModal(true);
    const handleBlur = () => setIsFocused(false);
    const handleBlurModal = () => setIsFocusedModal(false);
    const handleChange = (
        selectedOption: ((newValue: any, actionMeta: ActionMeta<any>) => void) | undefined | null,
    ) => {
        onSelect(selectedOption);
        setIsFocused(!!selectedOption);
    };
    const handleChangeModalOptions = (
        selectedOption: ((newValue: any, actionMeta: ActionMeta<any>) => void) | undefined | null,
    ) => {
        onSelect(selectedOption);
        setIsFocused(false);
        setIsFocusedModal(false);
    };

    const handleInputChange = (value: string) => {
        if (originalOptions && value) {
            setOptions(originalOptions);
        } else {
            setOptions(initialOptionsFiltered as any);
        }
        setInputValue(value);
    };

    const customStyles = {
        control: (provided: any, state: { isFocused: any }) => ({
            ...provided,
            paddingInline: '10px',
            border: state.isFocused ? '2px solid #2E2D37' : '1px solid #2E2D37',
            borderRadius: '8px',
            width: '312px',
            height: '48px',
            boxShadow: state.isFocused ? '0 0 0 1px #2E2D37' : 'none',
            '&:hover': {
                cursor: 'pointer',
            },
            position: 'relative',
        }),
        option: (
            provided: any,
            state: {
                data(data: any): unknown;
                selectProps: any;
                isFocused: any;
                isSelected: any;
            },
        ) => ({
            ...provided,
            zIndex: 2,
            borderBottom:
                separatorIndex > 0 && !inputValue && state.selectProps.options.indexOf(state.data) === separatorIndex
                    ? '1px solid #EDECF2'
                    : 'none',
            backgroundColor: state.isSelected ? '#EDECF2' : state.isFocused ? '#EDECF2' : 'white',
            color: '#2E2D37',
            '&:hover': {
                backgroundColor: '#EDECF2',
            },
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: '#6A6977',
        }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 5,
            boxShadow: isFocused && isMobile ? 'none' : provided.boxShadow,
            border: isFocused && isMobile ? 'none' : provided.border,
        }),
    };

    const ClearIndicator = (props) => {
        const {
            children = (
                <Image
                    alt="Ícone de limpar"
                    title="Limpar"
                    width={10}
                    height={10}
                    src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-closed.svg`}
                />
            ),
            getStyles,
            innerProps: { ref, ...restInnerProps },
        } = props;
        return (
            <div {...restInnerProps} ref={ref} style={getStyles('clearIndicator', props)}>
                {children}
            </div>
        );
    };

    return (
        <>
            <label
                htmlFor={id}
                data-testid={`${id}-label`}
                id={`${id}-label`}
                style={{
                    position: 'absolute',
                    top: isFocused || selectValue ? '-7px' : '16px',
                    left: '10px',
                    fontSize: isFocused || selectValue ? '12px' : '14px',
                    backgroundColor: 'white',
                    paddingInline: isFocused || selectValue ? '3px' : '0px',
                    marginInline: isFocused || selectValue ? '7px' : '10px',
                    marginBottom: '-10px',
                    color: isDisabled ? '#D0CFDA' : '#2E2D37',
                    transition: 'top 0.2s, font-size 0.2s',
                    pointerEvents: 'none',
                    zIndex: 1,
                }}
                className="custom-placeholder"
            >
                {placeholder}
            </label>

            {!isMobile ? (
                <Select
                    instanceId={id}
                    value={selectValue}
                    isDisabled={isDisabled}
                    placeholder={''}
                    className={`select-dropdown-input-filter ${isDisabled ? 'disabled' : ''}`}
                    classNamePrefix="custom-select"
                    noOptionsMessage={() => 'Nenhum resultado encontrado'}
                    options={options || []}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    styles={customStyles}
                    aria-labelledby={`${id}-label`}
                    isSearchable={true}
                    menuShouldScrollIntoView={false}
                    isLoading={isLoading}
                    components={{
                        LoadingIndicator: () => <LoadingSpinner />,
                    }}
                    data-testid={`${id}-select`}
                    id={`${id}-select`}
                />
            ) : (
                <>
                    <button
                        className="container-input-modal"
                        onClick={() => !isDisabled && setIsFocused(true)}
                        onKeyDown={() => !isDisabled && setIsFocused(true)}
                        data-testid={`${id}-modal-button`}
                        id={`${id}-modal-button`}
                    >
                        <div className="content-input-modal">
                            <span
                                className="select-value-text"
                                data-testid={`${id}-select-value`}
                                id={`${id}-select-value`}
                            >
                                {selectValue ? selectValue.label : ''}
                            </span>

                            <div
                                style={{ marginLeft: 'auto', opacity: isDisabled ? 0.36 : 1 }}
                                data-testid={`${id}-arrow-icon`}
                                id={`${id}-arrow-icon`}
                            >
                                <Image
                                    alt="Ícone de seta para baixo"
                                    title="Seta para baixo"
                                    width={24}
                                    height={24}
                                    src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-arrow-down.svg`}
                                />
                            </div>
                        </div>
                    </button>
                    <Modal
                        isOpen={isFocused && isMobile}
                        onRequestClose={() => setIsFocused(false)}
                        style={{
                            content: {
                                top: '0',
                                left: '0',
                                right: '0',
                                bottom: '0',
                                padding: '0',
                                backgroundColor: 'white',
                                zIndex: '10000',
                            },
                            overlay: {
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: '9999',
                            },
                        }}
                        ariaHideApp={false}
                        data-testid={`${id}-modal`}
                        id={`${id}-modal`}
                    >
                        <div className="title-modal" data-testid={`${id}-modal-title`} id={`${id}-modal-title`}>
                            <h3>{placeholderTitle}</h3>
                            <button
                                onClick={() => setIsFocused(false)}
                                data-testid={`${id}-modal-close-button`}
                                id={`${id}-modal-close-button`}
                            >
                                <Image
                                    alt="Ícone de limpar"
                                    title="Limpar"
                                    width={18}
                                    height={18}
                                    src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-closed.svg`}
                                />
                            </button>
                        </div>

                        <div className="content-modal" data-testid={`${id}-modal-content`} id={`${id}-modal-content`}>
                            <label
                                htmlFor={id}
                                data-testid={`${id}-modal-label`}
                                id={`${id}-modal-label`}
                                style={{
                                    position: 'relative',
                                    top: isFocusedModal || selectValue ? '9px' : '32px',
                                    left: '10px',
                                    fontSize: isFocusedModal || selectValue ? '12px' : '14px',
                                    backgroundColor: 'white',
                                    paddingInline: isFocusedModal || selectValue ? '3px' : '0px',
                                    marginInline: isFocusedModal || selectValue ? '7px' : '10px',
                                    marginBottom: '-10px',
                                    color: isDisabled ? '#D0CFDA' : '#2E2D37',
                                    transition: 'top 0.2s, font-size 0.2s',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                }}
                                className="custom-placeholder"
                            >
                                {placeholder}
                            </label>
                            <Select
                                instanceId={id}
                                value={selectValue}
                                isDisabled={isDisabled}
                                className="select-modal-input"
                                classNamePrefix="custom-select"
                                placeholder={''}
                                noOptionsMessage={() => 'Nenhum resultado encontrado'}
                                options={options || []}
                                onChange={handleChangeModalOptions}
                                onInputChange={handleInputChange}
                                styles={customStyles}
                                aria-labelledby={`${id}-modal-label`}
                                isSearchable={true}
                                onFocus={handleFocusModal}
                                onBlur={handleBlurModal}
                                autoFocus
                                components={{
                                    LoadingIndicator: () => <LoadingSpinner />,
                                    ClearIndicator,
                                    DropdownIndicator: () => null,
                                }}
                                isClearable
                                menuIsOpen
                                menuShouldScrollIntoView={false}
                                isLoading={isLoading}
                                data-testid={`${id}-modal-select`}
                                id={`${id}-modal-select`}
                            />
                        </div>
                    </Modal>
                </>
            )}
        </>
    );
};

export default Dropdown;
